<script lang="ts" module>
  import {
    type CheckoutActions,
    type CheckoutSdk,
    type CheckoutState,
  } from '@athena-lib/Checkout/utils';

  const mapOptional = <T, B>(value: T | undefined | null, fn: (value: T) => B) => {
    if (value === undefined || value === null) {
      return value;
    }
    return fn(value);
  };
  const getCustomStickerAttributes = (
    lineItem: CheckoutState['lines'][number],
    checkoutAtlasTranslations:
      | {
          translateMaterial: (material: string) => string;
          translateLaminate: (laminate: string) => string;
          translateCutDirection: (direction: string) => string;
          translateShape: (path: string) => string;
        }
      | undefined,
    locale: LocalizationContext,
    getCustomSticker: CheckoutSdk['getCustomSticker'],
    actions?: { onEdit: () => Promise<unknown>; onDuplicate: () => Promise<unknown> },
  ): {
    size: string;
    material: string;
    laminate: string;
    cutDirection?: string;
    translatedCutDirection?: string;
    priceList?: CartLineCustomStickerContent['priceList']['prices'];
    minQuantity: number;
    note?: string;
    translatedShape?: string;
    customStickerPromise?: ReturnType<CheckoutSdk['getCustomSticker']>;
    onEdit?: () => Promise<unknown>;
    onDuplicate?: () => Promise<unknown>;
  } => {
    if (lineItem.content.type !== 'customSticker') {
      return {
        size: '',
        material: '',
        laminate: '',
        minQuantity: 1,
      };
    }

    const material = mapOptional(
      lineItem.content.attributes['material'] as null | undefined | string,
      checkoutAtlasTranslations?.translateMaterial ?? ((x: string): string => x),
    );
    const laminate = mapOptional(
      lineItem.content.attributes['laminate'] as null | undefined | string,
      checkoutAtlasTranslations?.translateLaminate ?? ((x: string): string => x),
    );
    const translatedCutDirection = mapOptional(
      lineItem.content.attributes['cut_direction'] as null | undefined | string,
      checkoutAtlasTranslations?.translateCutDirection ?? ((x: string): string => x),
    );
    const width = lineItem.content.attributes['width_mm'];
    const height = lineItem.content.attributes['height_mm'];
    const size =
      typeof width === 'number' && typeof height === 'number'
        ? formatSize(width, height, locale, true)
        : '';
    const priceList = lineItem.content.priceList?.prices;
    const noteMaybe = lineItem.content.attributes.note;
    const translatedShape =
      lineItem.content.productName === 'library_design'
        ? undefined
        : mapOptional(
            lineItem.content.attributes.sheet_name as null | undefined | string,
            checkoutAtlasTranslations?.translateShape ?? ((x: string): string => x),
          );

    return {
      size: size,
      material: material ?? '',
      laminate: laminate ?? '',
      cutDirection:
        lineItem.content.productName === 'labels_on_roll'
          ? (lineItem.content.attributes['cut_direction'] as undefined | string)
          : undefined,
      translatedCutDirection: translatedCutDirection ?? undefined,
      translatedShape: translatedShape ?? '',
      priceList,
      ...(lineItem.content.productName !== 'library_design' ? actions : {}),
      minQuantity: Math.min(...priceList.map((x) => x.quantity)),
      note: typeof noteMaybe === 'string' ? noteMaybe : undefined,
      customStickerPromise: getCustomSticker(
        lineItem.content.customStickerId,
        lineItem.content.customStickerVersion,
      ),
    };
  };
  export type QuoteCardProps = {
    id: string;
    image: {
      src: string;
      alt: string;
    };
    type: CartLine['content']['type'];
    name: string;
    quantity: number;
    totalPrice: string;
    pricePerUnit: string;
    size?: string;
    material?: string;
    minQuantity: number;
    laminate?: string;
    note?: string;
    cutDirection?: string;
    translatedCutDirection?: string;
    translatedShape?: string;
    priceList?: CartLineCustomStickerContent['priceList']['prices'];
    customStickerPromise?: ReturnType<CheckoutSdk['getCustomSticker']>;
    onQuantityChange?: (quantity: number) => Promise<unknown>;
    onRemove?: () => Promise<unknown>;
    onDuplicate?: () => Promise<unknown>;
    onEdit?: () => Promise<unknown>;
    onPreview?: () => Promise<unknown>;
    onItemClick?: (id: string) => void;
    isBundle?: boolean;
    cartType?: 'mini' | 'full';
    isManualOrder?: boolean;
    isBackPaperPrint?: boolean;
  };

  export const lineItemToQuoteCardBase = (
    lineItem: CheckoutState['lines'][number],
    taxType: 'vat' | 'salesTax' | null | undefined,
    formatPrice: (price: number) => string,
    getCustomSticker: CheckoutSdk['getCustomSticker'],
    checkoutAtlasTranslations:
      | {
          translateMaterial: (material: string) => string;
          translateLaminate: (laminate: string) => string;
          translateCutDirection: (direction: string) => string;
          translateShape: (path: string) => string;
        }
      | undefined,
    locale: LocalizationContext,
    actions?: { onEdit: () => Promise<unknown>; onDuplicate: () => Promise<unknown> },
  ) => {
    const priceKey = taxType === 'salesTax' ? 'amountExclTax' : 'amountInclTax';
    return {
      id: lineItem.id,
      pricePerUnit: formatPrice(
        lineItem.price.unitOriginal?.major[priceKey] ?? lineItem.price.unit.major[priceKey],
      ),
      totalPrice: formatPrice(
        lineItem.price.totalOriginal?.major[priceKey] ?? lineItem.price.total.major[priceKey],
      ),
      image: { src: lineItem.content.imageUrl ?? '', alt: '' },
      name: lineItem.content.name,
      ...getCustomStickerAttributes(
        lineItem,
        checkoutAtlasTranslations,
        locale,
        getCustomSticker,
        actions,
      ),
      quantity: lineItem.quantity,
      type: lineItem.content.type,
      isBundle: lineItem.price.bundleSavings && lineItem.price.bundleSavings.major > 0,
      isBackPaperPrint: Array.isArray(lineItem.content.attributes.feature)
        ? lineItem.content.attributes.feature.includes('backpaper_print')
        : false,
    };
  };

  export const lineItemToQuoteCard = (
    lineItem: CheckoutState['lines'][number],
    taxType: 'vat' | 'salesTax' | null | undefined,
    formatPrice: (price: number) => string,
    checkoutActions: CheckoutActions,
    gtmTrackingEvents: {
      onEdit: () => void;
      onChangeQuantity: ({
        change_from,
        change_to,
      }: {
        change_from: number;
        change_to: number;
      }) => void;
      onDuplicate: () => void;
      onRemove: () => void;
    },
    onOpenEditor: (lineItem: CheckoutState['lines'][number]) => void,
    getCustomSticker: (id: string) => Promise<CustomStickerWithId | undefined>,
    checkoutAtlasTranslations:
      | {
          translateMaterial: (material: string) => string;
          translateLaminate: (laminate: string) => string;
          translateCutDirection: (direction: string) => string;
          translateShape: (path: string) => string;
        }
      | undefined,
    locale: LocalizationContext,
    commonTranslations: CommonTranslations,
  ): QuoteCardProps => {
    return {
      onQuantityChange: (quantity) => {
        const previousQuantity = lineItem.quantity;
        return checkoutActions.updateLineItem(lineItem.id, quantity).then((x) => {
          if (!x.success) {
            throw new Error(x.translatedErrorCode ?? commonTranslations.anErrorHasOccured);
          }
          gtmTrackingEvents.onChangeQuantity({
            change_from: previousQuantity,
            change_to: quantity,
          });
        });
      },
      onRemove: () =>
        checkoutActions.removeLineItem(lineItem.id).then((x) => {
          if (!x.success) {
            throw new Error('Failed to remove line item');
          }
          gtmTrackingEvents.onRemove();
        }),
      ...lineItemToQuoteCardBase(
        lineItem,
        taxType,
        formatPrice,
        getCustomSticker,
        checkoutAtlasTranslations,
        locale,
        {
          onDuplicate: () =>
            checkoutActions.duplicateCustomStickerLineItem(lineItem.id).then((x) => {
              if (!x.success) {
                throw new Error('Failed to duplicate custom sticker line item');
              }
              gtmTrackingEvents.onDuplicate();
            }),
          onEdit: () => {
            onOpenEditor(lineItem);
            gtmTrackingEvents.onEdit();
            return Promise.resolve(null);
          },
        },
      ),
    };
  };
</script>

<script lang="ts">
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import TextField from '@athena-lib/Fields/TextField/TextField.svelte';
  import type { LocalizationContext } from '@athena-lib/i18n';
  import { Copy, Delete, Edit, Spinner, Visibility } from '@athena-lib/Icons';
  import MutationButton from '@athena-lib/MutationButton/MutationButton.svelte';
  import { formatSize } from '@athena-lib/ProductWizard';
  import { Text } from '@athena-lib/Text';
  import { useMutation } from '@athena-lib/useMutation.svelte';
  import type {
    CartLine,
    CartLineCustomStickerContent,
    CustomStickerWithId,
  } from '@stickerapp-org/checkout-sdk';
  import { Image } from '@athena-lib/Image';
  import { LiveLineItemStickerPreview } from '@athena-lib/LivePreview';
  import ComboboxSuggestions from '@athena-lib/Fields/TextField/ComboboxSuggestions.svelte';
  import clsx from 'clsx';
  import { extractErrorMessage } from '@athena-lib/extractErrorMessage';
  import { type CommonTranslations } from '@athena-lib/commonTranslationsContext';
  import { Column, Columns } from '@athena-lib/Columns';
  import Chat from '@athena-lib/Icons/Chat.svelte';
  import StackItem from '@athena-lib/Stack/StackItem.svelte';
  import Stack from '@athena-lib/Stack/Stack.svelte';
  import MoreVert from '@athena-lib/Icons/MoreVert.svelte';
  import Popover from '@athena-lib/Popover/Popover.svelte';
  import debouncedFunc from '@athena-lib/timing-utils';
  import { interpolate } from '@athena-lib/Trans/Trans.svelte';
  import DesignServices from '@athena-lib/Icons/DesignServices.svelte';

  const {
    id,
    image,
    type,
    laminate,
    name,
    quantity,
    totalPrice,
    pricePerUnit,
    size,
    material,
    translatedShape,
    priceList = [],
    note,
    minQuantity,
    onQuantityChange,
    onRemove,
    onDuplicate,
    onEdit,
    onPreview,
    isBundle,
    customStickerPromise,
    cartType,
    onItemClick,
    isManualOrder,
    cutDirection,
    translatedCutDirection,
    isBackPaperPrint,
  }: QuoteCardProps = $props();

  const actions = $derived.by(() => {
    if (isManualOrder) {
      return [];
    }
    return [
      { action: onEdit, label: translations.edit, id: 'edit' },
      { action: onDuplicate, label: translations.duplicate, id: 'duplicate' },
      { action: onRemove, label: translations.remove, id: 'remove' },
    ].filter((x): x is { action: typeof onEdit; label: string; id: string } => !!x.action);
  });

  const textSize = $derived.by(() => {
    if (cartType === 'mini') {
      return 'small';
    }
    return 'medium';
  });

  function clickOutside(node: HTMLElement, params: { ignore?: HTMLElement; callback: () => void }) {
    function handleClick(event: MouseEvent) {
      const target = event.target as Node;
      if (!node.contains(target) && !(params.ignore && params.ignore.contains(target))) {
        params.callback();
      }
    }

    document.addEventListener('click', handleClick, true);

    return {
      destroy() {
        document.removeEventListener('click', handleClick, true);
      },
    };
  }

  const translations = getCommonTranslations();

  let localQuantity = $state(quantity);

  const quantityError = $derived.by(() => {
    if (quantityMutation.state.type === 'rejected') {
      const translation =
        extractErrorMessage(quantityMutation.state.error) ?? translations.anErrorHasOccured;
      return interpolate(translation, { minQuantity: minQuantity.toString() });
    }
    return undefined;
  });
  let optionsOpen = $state(false);
  let triggerBtn: HTMLButtonElement | undefined = $state(undefined);

  let latestMutation = $state(0);
  const quantityMutation = useMutation((value: number) => {
    if (value < minQuantity) {
      return Promise.reject(translations.quantityMustBeGreaterThanMinimum);
    }
    if (!onQuantityChange) {
      return Promise.reject('not defined');
    }
    const myMutation = latestMutation++;
    return onQuantityChange(value).finally(() => {
      if (latestMutation === myMutation) {
        localQuantity = quantity;
      }
    });
  });
  const debouncedMutation = debouncedFunc((quantity: number) => {
    quantityMutation.mutate(quantity);
  }, 300);

  let quantityElement = $state<HTMLInputElement | undefined>();
  const translatedName = $derived(translations[name as keyof typeof translations] ?? name);
</script>

{#snippet Actions()}
  <div class="flex flex-1 justify-end" class:hidden={actions.length === 0}>
    <button
      bind:this={triggerBtn}
      type="button"
      class="rounded-full p-1 text-st-gray-800"
      onclick={(e) => {
        e.stopPropagation();
        optionsOpen = !optionsOpen;
      }}
    >
      <MoreVert sizeClass="h-8 w-8" />
    </button>
    {#if optionsOpen}
      <div
        use:clickOutside={{ ignore: triggerBtn, callback: () => (optionsOpen = false) }}
        class="absolute right-0 z-50 mt-10 flex w-auto flex-col items-start gap-1 rounded border bg-st-white shadow-lg"
      >
        {#each actions as { action, label, id }}
          {#if action}
            <MutationButton
              unstyled
              wide={false}
              class="flex w-full items-center gap-1 p-3 text-xs text-st-gray-500 hover:bg-st-gray-100"
              createPromise={() => {
                return action().then(() => (optionsOpen = !optionsOpen));
              }}
              title={label}
              aria-label={label}
            >
              {#if id === 'edit'}
                <Edit sizeClass="h-6 w-6"></Edit>
                <Text class="pl-2">{translations.edit}</Text>
              {:else if id === 'duplicate'}
                <Copy sizeClass="h-6 w-6"></Copy>
                <Text class="pl-2">{translations.duplicate}</Text>
              {:else if id === 'remove'}
                <Delete sizeClass="h-6 w-6"></Delete>
                <Text class="pl-2">{translations.delete}</Text>
              {/if}
            </MutationButton>
          {/if}
        {/each}
      </div>
    {/if}
  </div>
{/snippet}
<li
  {id}
  class={clsx(
    cartType === 'mini' ? 'h-full' : 'h-[14rem] md:h-64',
    'border-1 border-st-grey-300 relative flex flex-col rounded-lg border bg-st-white',
  )}
>
  <Columns space="none" class="h-full">
    <Column
      width={cartType === 'mini'
        ? { sm: '2/5', md: '2/5', lg: '2/5' }
        : { sm: '1/3', md: '1/2', lg: '1/2' }}
      class={clsx(
        cartType != 'mini' ? 'h-[14rem] md:h-64' : '',
        'aspect-square basis-1/3 content-center justify-items-center rounded-l-lg bg-st-gray-200 text-center',
      )}
    >
      <button
        class={cartType === 'mini' ? 'cursor-auto' : ''}
        type="button"
        onclick={() => (onItemClick ? onItemClick(id) : undefined)}
      >
        {#if note}
          <div class="absolute left-2 top-2 z-10">
            <Chat sizeClass="h-6 w-6"></Chat>
          </div>
        {/if}
        <div class={clsx(cartType === 'mini' ? 'max-h-48' : 'max-h-48 md:max-h-64', 'flex')}>
          {#if type === 'customFee'}
            <DesignServices sizeClass="h-auto w-full max-h-36" />
          {:else if type === 'customSticker' && !image.src && customStickerPromise}
            <!-- TODO: Remove check for image.src once we have the correct parts on sticker shop item -->
            <!-- TODO: Insert specific conditions on showLivePreview -->
            <LiveLineItemStickerPreview
              class={clsx(cartType === 'mini' ? 'max-h-48' : 'max-h-48 md:max-h-64')}
              {customStickerPromise}
            />
          {:else if image}
            <Image src={image.src} alt={image.alt} class="aspect-auto max-w-full" />
          {/if}
        </div>
      </button>
    </Column>
    <Column
      width="3/4"
      class={clsx(
        cartType === 'mini'
          ? 'flex flex-col flex-wrap justify-between pb-[20px] pl-[15px] pr-[15px] pt-[20px]'
          : 'pl-4 md:pl-8',
        'relative flex flex-col p-3 md:p-4',
      )}
    >
      <Stack space={cartType === 'mini' ? 'none' : { sm: 'none', md: 'xlarge' }}>
        <StackItem>
          <div class="flex">
            {#if type === 'customFee'}
              <Text element="span" size="large">{name}</Text>
            {:else if isBundle}
              <div class="flex flex-row items-center gap-2 truncate">
                <Text element="span" size="large" class="truncate" bold>{translatedName}</Text>
                <div class="flex flex-auto">
                  <span class="rounded bg-st-green-400 px-1 text-xs text-st-white"
                    >{translations.bundle}</span
                  >
                </div>
              </div>
            {:else}
              <Text element="span" size="large" class="truncate" bold>{translatedName}</Text>
            {/if}
            {#if actions.length > 0 && cartType !== 'mini'}
              {@render Actions()}
            {/if}
          </div>
          {#if type === 'customSticker'}
            <div
              class={clsx(
                cartType === 'mini' ? 'mt-[11px]' : '',
                'flex flex-row flex-wrap justify-between gap-2',
              )}
            >
              <div class="flex flex-col justify-between">
                <div class="flex flex-col gap-0.5">
                  <Text element="span" size={textSize} color="dark">{size}</Text>
                  <Text class="line-clamp-1" element="span" size={textSize} color="dark"
                    >{material} / {laminate}</Text
                  >
                  <Text element="span" size={textSize} color="dark">{translatedShape}</Text>
                  {#if cartType === 'mini'}
                    <Text element="span" size={textSize} color="dark"
                      >{quantity} {translations.pcs}</Text
                    >
                  {/if}
                  {#if isBackPaperPrint}
                    <Text element="span" size={textSize} color="dark"
                      >{translations.backPaperPrint}</Text
                    >
                  {/if}
                  {#if cutDirection}
                    <div class="flex items-center gap-2">
                      <Text element="span" size={textSize} color="dark"
                        >{@html translatedCutDirection?.replaceAll('_', ' ')}</Text
                      >
                    </div>
                  {/if}
                </div>
              </div>
            </div>
          {/if}
        </StackItem>
        {#if cartType === 'mini' || type === 'customFee'}
          <Text class="absolute bottom-[18px] right-[1rem] text-lg font-bold">{totalPrice}</Text>
        {:else}
          <StackItem>
            <div
              class={clsx(
                'absolute bottom-5 left-0 right-0 flex flex-row items-end justify-between px-4 md:px-8',
              )}
            >
              <div class="flex flex-col gap-3">
                {#if isManualOrder}
                  <div>
                    {quantity}
                    {translations.pcs}
                  </div>
                {:else}
                  <div class="flex flex-1 flex-col gap-4">
                    {#if onQuantityChange}
                      {@const mappedPriceList = priceList.map((x) => ({
                        label: x,
                        onClick: () => {
                          debouncedMutation(Number(x.quantity));
                          localQuantity = x.quantity;
                        },
                      }))}
                      {@const maxUnitPrice = Math.max(...priceList.map((x) => x.unitPrice))}
                      {@const hasRebates = priceList.find((x) => x.unitPrice !== maxUnitPrice)}
                      <ComboboxSuggestions
                        placement="center"
                        inputElement={quantityElement}
                        suggestions={mappedPriceList}
                      >
                        {#snippet renderSuggestion(label)}
                          {@const priceDrop = 1 - label.unitPrice / maxUnitPrice}
                          <div
                            class={clsx(
                              'flex w-full flex-row p-2 ',
                              label.quantity === localQuantity ? 'bg-gray-100 font-bold' : '',
                            )}
                          >
                            <div class="flex w-24 flex-row justify-center">
                              {label.quantity}
                              {translations.pcs}
                            </div>
                            <div class="flex w-24 flex-row justify-center">
                              {label.price.totalFormatted}
                            </div>
                            {#if hasRebates}
                              <div class="flex w-24 flex-row justify-center">
                                {#if priceDrop > 0}
                                  <div class={clsx('arrow-pill')}>
                                    -{priceDrop.toLocaleString(undefined, {
                                      style: 'percent',
                                      minimumFractionDigits: 0,
                                    })}
                                  </div>
                                {/if}
                              </div>
                            {/if}
                          </div>
                        {/snippet}
                        {#snippet children(snippet, onKeyDown)}
                          <Popover trigger={quantityError ? 'always' : 'hidden'}>
                            {#snippet floater()}
                              <div class="inline-block rounded bg-st-black px-2 py-1 text-st-white">
                                {quantityError}
                              </div>
                            {/snippet}
                            <TextField
                              min={minQuantity ?? 1}
                              bind:inputElement={quantityElement}
                              disabled={false}
                              type="number"
                              class={'w-20 md:w-28'}
                              onkeydown={onKeyDown}
                              name="quantity"
                              helperElement={snippet}
                              label={''}
                              bind:value={localQuantity}
                              onchange={(e) => {
                                debouncedMutation(Number(e.currentTarget.value));
                              }}
                              aria-label={translations.quantity}
                            />
                          </Popover>
                        {/snippet}
                      </ComboboxSuggestions>
                    {:else}
                      <Text element="span" size="small">{quantity} {translations.pcs}</Text>
                    {/if}
                  </div>
                {/if}
              </div>
              {#if quantityMutation.state.type === 'loading'}
                <Spinner sizeClass={'h-8 w-8 self-center'} />
              {:else}
                <div
                  class="mr-2 flex flex-col justify-center self-end break-all pt-2 text-right md:pt-0"
                >
                  <Text element="span" size={{ sm: 'medium', md: 'medium', lg: 'large' }} bold
                    >{totalPrice}</Text
                  >
                  <Text element="span" size="small" color="dark"
                    >{pricePerUnit}/{translations.pcs}</Text
                  >
                </div>
              {/if}
            </div>
          </StackItem>
        {/if}
      </Stack>
    </Column>
  </Columns>

  <div class="relative">
    <button
      type="button"
      class:hidden={!onPreview}
      class="absolute right-0 top-10 rounded-full bg-st-white p-1 shadow-lg outline-none"
      aria-label={translations.preview}
    >
      <Visibility sizeClass="size-4" />
    </button>
  </div>
</li>

<style>
  .arrow-pill {
    position: relative;
    display: inline-block;
    background-color: #78b480; /* The green color */
    color: white;
    padding: 4px 4px;
    padding-right: 6px;
    border-radius: 0px;
    border-top-right-radius: 2px;
    border-bottom-right-radius: 2px;
  }

  .arrow-pill:before {
    content: '';
    position: absolute;
    left: -12px;
    top: 50%;
    transform: translateY(-50%);
    border-top: 14px solid transparent;
    border-radius: 0px;
    border-bottom: 14px solid transparent;
    border-right: 12px solid #78b480;
  }
</style>
